import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import useGetMutationFn from '../use-get-mutation-fn';
import { APPS_CATALOG_BASE, appsCatalogKeys, unwrapEnvelope } from './apps-catalog.query';
import type {
  AdminCatalogApp,
  AppCatalogAppWriteResult,
  AppCatalogSection,
  AppCatalogStatusResult,
  CreateAppCatalogAppPayload,
  CreateAppCatalogSectionPayload,
  ReorderAppCatalogAppsPayload,
  SetAppCatalogStatusPayload,
  UpdateAppCatalogAppPayload,
  UpdateAppCatalogSectionPayload,
} from '@/client/types/apps-catalog';

type QC = ReturnType<typeof useQueryClient>;

const TIMEOUT = { timeout: 30_000 };

/**
 * The 400 `fields` map ({field: reason}) from an apps-catalog refusal, or {}.
 * The thrown error carries the proxied envelope on `data`.
 */
export const getAppsCatalogFieldErrors = (error: unknown): Record<string, string> => {
  const body = (error as any)?.data;
  const fields = body?.data?.fields ?? body?.fields ?? body?.data?.data?.fields;
  if (fields == null || typeof fields !== 'object') return {};
  return Object.fromEntries(
    Object.entries(fields as Record<string, unknown>).map(([k, v]) => [k, String(v)]),
  );
};

const normalizeWarnings = (payload: any): string[] =>
  Array.isArray(payload?.warnings) ? payload.warnings.map(String) : [];

// Any write can change what the preview shows, and the sections list carries
// app counts, so both are invalidated alongside the list that changed.
const invalidateApps = (qc: QC, appKey?: string) => {
  qc.invalidateQueries({ queryKey: [...appsCatalogKeys.all, 'apps'] });
  if (appKey) qc.invalidateQueries({ queryKey: appsCatalogKeys.app(appKey) });
  qc.invalidateQueries({ queryKey: appsCatalogKeys.sections() });
  qc.invalidateQueries({ queryKey: [...appsCatalogKeys.all, 'preview'] });
};

const invalidateSections = (qc: QC) => {
  qc.invalidateQueries({ queryKey: appsCatalogKeys.sections() });
  // Section order drives the apps list's catalog order.
  qc.invalidateQueries({ queryKey: [...appsCatalogKeys.all, 'apps'] });
  qc.invalidateQueries({ queryKey: [...appsCatalogKeys.all, 'preview'] });
};

// ─── Sections ────────────────────────────────────────────────────────────────

export const useCreateAppsCatalogSectionMutation = () => {
  const qc = useQueryClient();
  const fn = useGetMutationFn<CreateAppCatalogSectionPayload, unknown>({
    endpoint: `${APPS_CATALOG_BASE}/sections`,
    method: 'POST',
    defaultErrorMessage: 'Failed to create section',
  });
  return useMutation({
    mutationFn: async (payload: CreateAppCatalogSectionPayload): Promise<AppCatalogSection> =>
      unwrapEnvelope(await fn(payload))?.section,
    onSuccess: () => invalidateSections(qc),
  });
};

export const useUpdateAppsCatalogSectionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  // The key is in the path only: the body must not carry it (the API refuses).
  const fn = withErrorHandling(async ({ key, changes }: UpdateAppCatalogSectionPayload) => {
    const raw = await request<unknown>(
      `${APPS_CATALOG_BASE}/sections/${encodeURIComponent(key)}`,
      'PUT',
      changes,
      TIMEOUT,
    );
    return unwrapEnvelope(raw)?.section as AppCatalogSection;
  }, 'Failed to update section');
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateSections(qc),
  });
};

export const useReorderAppsCatalogSectionsMutation = () => {
  const qc = useQueryClient();
  const fn = useGetMutationFn<{ keys: string[] }, unknown>({
    endpoint: `${APPS_CATALOG_BASE}/reorder/sections`,
    method: 'PUT',
    defaultErrorMessage: 'Failed to reorder sections',
  });
  return useMutation({
    mutationFn: async (keys: string[]): Promise<string[]> => {
      const payload = unwrapEnvelope(await fn({ keys }));
      return Array.isArray(payload?.keys) ? payload.keys : keys;
    },
    onSuccess: () => invalidateSections(qc),
  });
};

// ─── Apps ────────────────────────────────────────────────────────────────────

export const useCreateAppsCatalogAppMutation = () => {
  const qc = useQueryClient();
  const fn = useGetMutationFn<CreateAppCatalogAppPayload, unknown>({
    endpoint: `${APPS_CATALOG_BASE}/apps`,
    method: 'POST',
    defaultErrorMessage: 'Failed to create app',
  });
  return useMutation({
    mutationFn: async (payload: CreateAppCatalogAppPayload): Promise<AppCatalogAppWriteResult> => {
      const data = unwrapEnvelope(await fn(payload));
      return { app: data?.app as AdminCatalogApp, warnings: normalizeWarnings(data) };
    },
    onSuccess: (result) => invalidateApps(qc, result.app?.app_key),
  });
};

export const useUpdateAppsCatalogAppMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  // app_key rides in the path; the body is only the changed editable fields
  // (the API refuses app_key and status on this route).
  const fn = withErrorHandling(async ({ app_key, changes }: UpdateAppCatalogAppPayload) => {
    const raw = await request<unknown>(
      `${APPS_CATALOG_BASE}/apps/${encodeURIComponent(app_key)}`,
      'PUT',
      changes,
      TIMEOUT,
    );
    return unwrapEnvelope(raw)?.app as AdminCatalogApp;
  }, 'Failed to update app');
  return useMutation({
    mutationFn: fn,
    onSuccess: (_app, vars) => invalidateApps(qc, vars.app_key),
  });
};

export const useSetAppsCatalogAppStatusMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async ({ app_key, status }: SetAppCatalogStatusPayload): Promise<AppCatalogStatusResult> => {
      const raw = await request<unknown>(
        `${APPS_CATALOG_BASE}/apps/${encodeURIComponent(app_key)}/status`,
        'POST',
        { status },
        TIMEOUT,
      );
      const data = unwrapEnvelope(raw);
      return {
        app: data?.app as AdminCatalogApp,
        changed: Boolean(data?.changed),
        warnings: normalizeWarnings(data),
      };
    },
    'Failed to change app status',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_result, vars) => invalidateApps(qc, vars.app_key),
  });
};

export const useReorderAppsCatalogAppsMutation = () => {
  const qc = useQueryClient();
  const fn = useGetMutationFn<ReorderAppCatalogAppsPayload, unknown>({
    endpoint: `${APPS_CATALOG_BASE}/reorder/apps`,
    method: 'PUT',
    defaultErrorMessage: 'Failed to reorder apps',
  });
  return useMutation({
    mutationFn: async (payload: ReorderAppCatalogAppsPayload): Promise<string[]> => {
      const data = unwrapEnvelope(await fn(payload));
      return Array.isArray(data?.app_keys) ? data.app_keys : payload.app_keys;
    },
    onSuccess: () => invalidateApps(qc),
  });
};
